import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/shared';
import { styles } from './LanguageSwitcher.styles';

interface LanguageSwitcherProps {
  compact?: boolean;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ compact = false }) => {
  const { language, setLanguage, availableLanguages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLanguage = availableLanguages.find((lang) => lang.code === language);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  if (compact) {
    // Compact mode: simple toggle between languages
    const nextLanguage = availableLanguages.find((lang) => lang.code !== language);
    return (
      <button
        type="button"
        onClick={() => nextLanguage && setLanguage(nextLanguage.code)}
        style={styles.compactButton}
        title={nextLanguage?.name}
        aria-label={nextLanguage?.name ?? 'Switch language'}
      >
        <span style={styles.compactFlag}>{currentLanguage?.flag}</span>
        <span style={styles.compactCode}>{currentLanguage?.code.toUpperCase()}</span>
      </button>
    );
  }

  return (
    <div style={styles.container} ref={containerRef}>
      <button type="button" onClick={() => setIsOpen(!isOpen)} style={styles.button}>
        <span style={styles.flag}>{currentLanguage?.flag}</span>
        <span style={styles.code}>{currentLanguage?.code.toUpperCase()}</span>
        <span style={styles.arrow}>{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div style={styles.dropdown}>
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => {
                setLanguage(lang.code);
                setIsOpen(false);
              }}
              style={{
                ...styles.dropdownItem,
                ...(lang.code === language ? styles.dropdownItemActive : {}),
              }}
            >
              <span style={styles.dropdownFlag}>{lang.flag}</span>
              <span style={styles.dropdownName}>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
